import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SectionHelp from '@/components/help/SectionHelp';
import HelpTooltip from '@/components/help/HelpTooltip';
import { BedDouble, CalendarDays, Plus, X, LogIn, LogOut, Search, Tag, Percent, Sparkles, Receipt } from 'lucide-react';

// S118 (5/N) — Recepción del hotel.
//
// Está armada en el orden en que trabaja un hotel de verdad, no en el orden en
// que se construyó el backend:
//   1. Hoy — lo primero que abre en la mañana: quién llega, quién sale, cuánto
//      está ocupado.
//   2. Buscar y reservar — la pregunta que llega cien veces al día.
//   3. Reservas — el libro completo.
//   4. Habitaciones y Temporadas — configuración, se toca una vez al mes.

const ESTADOS = {
  pendiente: { texto: 'Por confirmar', clase: 'bg-amber-100 text-amber-800' },
  confirmada: { texto: 'Confirmada', clase: 'bg-emerald-100 text-emerald-800' },
  en_casa: { texto: 'Hospedado', clase: 'bg-blue-100 text-blue-800' },
  salida: { texto: 'Ya salió', clase: 'bg-slate-100 text-slate-600' },
  cancelada: { texto: 'Cancelada', clase: 'bg-rose-100 text-rose-700' },
  no_show: { texto: 'No llegó', clase: 'bg-rose-100 text-rose-700' },
};

const ESTADO_HABITACION = {
  disponible: 'En servicio',
  limpieza: 'Pendiente de limpieza',
  mantenimiento: 'En mantenimiento',
  fuera_de_servicio: 'Fuera de servicio',
};

function hoyISO() {
  return new Date().toISOString().slice(0, 10);
}

function enDias(dias) {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return d.toISOString().slice(0, 10);
}

function Etiqueta({ estado }) {
  const e = ESTADOS[estado] || { texto: estado, clase: 'bg-slate-100 text-slate-600' };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${e.clase}`}>{e.texto}</span>;
}

export default function HotelDesk() {
  const [pestana, setPestana] = useState('hoy');
  const [cargando, setCargando] = useState(true);

  const [ocupacion, setOcupacion] = useState(null);
  const [habitaciones, setHabitaciones] = useState([]);
  const [temporadas, setTemporadas] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [tipos, setTipos] = useState([]);

  // Búsqueda de disponibilidad
  const [busqueda, setBusqueda] = useState({ entrada: hoyISO(), salida: enDias(1), huespedes: 2 });
  const [disponible, setDisponible] = useState(null);
  const [buscando, setBuscando] = useState(false);

  const [reservando, setReservando] = useState(null);
  const [huesped, setHuesped] = useState({ guest_name: '', guest_phone: '', guest_email: '' });

  const [nuevaHab, setNuevaHab] = useState(null);
  const [nuevaTemp, setNuevaTemp] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [porLimpiar, setPorLimpiar] = useState([]);
  // S136 — la cuenta de una estancia: noches MÁS consumos. `null` = cerrada.
  const [cuenta, setCuenta] = useState(null);
  const [nuevoConsumo, setNuevoConsumo] = useState({ descripcion: '', cantidad: 1, precio_unitario: '' });

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [ocu, hab, tmp, res, lim, cat] = await Promise.all([
        api.get('/tenant/hotel/ocupacion'),
        api.get('/tenant/hotel/habitaciones'),
        api.get('/tenant/hotel/temporadas'),
        api.get('/tenant/hotel/reservas'),
        api.get('/tenant/hotel/limpieza'),
        // Los tipos de habitación son artículos del catálogo: se leen de ahí
        // en vez de duplicarlos, para que una sola alta sirva para todo.
        api.get('/tenant/catalog/items?item_type=room').catch(() => ({ data: { items: [] } })),
      ]);
      setOcupacion(ocu.data);
      setHabitaciones(hab.data?.habitaciones || []);
      setTemporadas(tmp.data?.temporadas || []);
      setReservas(res.data?.reservas || []);
      setPorLimpiar(lim.data?.habitaciones || []);
      setTipos(cat.data?.items || cat.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const buscar = async () => {
    setBuscando(true);
    setDisponible(null);
    try {
      const { data } = await api.get('/tenant/hotel/disponibilidad', { params: busqueda });
      setDisponible(data.disponibilidad || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBuscando(false);
    }
  };

  const reservar = async () => {
    if (!huesped.guest_name.trim()) { toast.error('Falta el nombre de quien se hospeda.'); return; }
    try {
      await api.post('/tenant/hotel/reservas', {
        catalog_item_id: reservando.catalog_item_id,
        check_in: busqueda.entrada, check_out: busqueda.salida,
        guests_count: Number(busqueda.huespedes) || 1,
        ...huesped, status: 'confirmada',
      });
      toast.success(`Reserva creada para ${huesped.guest_name}.`);
      setReservando(null);
      setHuesped({ guest_name: '', guest_phone: '', guest_email: '' });
      setDisponible(null);
      await cargar();
      setPestana('reservas');
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cambiarEstado = async (reserva, status, verbo) => {
    try {
      await api.post(`/tenant/hotel/reservas/${reserva.id}/estado`, { status });
      toast.success(`${verbo} — ${reserva.huesped}.`);
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const guardarHabitacion = async () => {
    if (!nuevaHab.catalog_item_id) { toast.error('Elige el tipo de habitación.'); return; }
    if (!nuevaHab.number?.trim()) { toast.error('Falta el número de la habitación.'); return; }
    try {
      await api.post('/tenant/hotel/habitaciones', nuevaHab);
      toast.success(`Habitación ${nuevaHab.number} dada de alta.`);
      setNuevaHab(null);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const guardarTemporada = async () => {
    if (!nuevaTemp.catalog_item_id) { toast.error('Elige a qué tipo de habitación aplica.'); return; }
    try {
      await api.post('/tenant/hotel/temporadas', {
        ...nuevaTemp, price_per_night: Number(nuevaTemp.price_per_night) || 0,
        priority: Number(nuevaTemp.priority) || 0,
      });
      toast.success('Temporada guardada.');
      setNuevaTemp(null);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const borrar = async (ruta, aviso) => {
    try {
      await api.delete(ruta);
      toast.success(aviso);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const abrirCuenta = async (r) => {
    try {
      const { data } = await api.get(`/tenant/hotel/reservas/${r.id}/cuenta`);
      setCuenta(data);
      setNuevoConsumo({ descripcion: '', cantidad: 1, precio_unitario: '' });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cargarConsumo = async () => {
    if (!nuevoConsumo.descripcion.trim()) { toast.error('Escribe qué consumió'); return; }
    try {
      const { data } = await api.post(`/tenant/hotel/reservas/${cuenta.reserva_id}/consumos`, {
        descripcion: nuevoConsumo.descripcion,
        cantidad: Number(nuevoConsumo.cantidad) || 1,
        precio_unitario: Number(nuevoConsumo.precio_unitario) || 0,
      });
      toast.success(data?.aviso || 'Cargado a su cuenta.');
      // Se relee la cuenta del servidor en vez de sumarlo aquí: el total es
      // dinero, y calcularlo dos veces (aquí y allá) es como se termina
      // enseñando una cifra distinta a la que se cobra.
      const { data: fresca } = await api.get(`/tenant/hotel/reservas/${cuenta.reserva_id}/cuenta`);
      setCuenta(fresca);
      setNuevoConsumo({ descripcion: '', cantidad: 1, precio_unitario: '' });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const anularConsumo = async (c) => {
    const motivo = window.prompt(
      `¿Por qué se quita «${c.descripcion}»?\n\n`
      + 'El cargo no se borra: queda anulado con este motivo, para que haya '
      + 'constancia si el huésped reclama.');
    if (motivo === null) return;
    if (!motivo.trim()) { toast.error('Hay que decir el motivo'); return; }
    try {
      await api.post(`/tenant/hotel/consumos/${c.id}/anular`, { motivo });
      const { data } = await api.get(`/tenant/hotel/reservas/${cuenta.reserva_id}/cuenta`);
      setCuenta(data);
      toast.success('Cargo anulado.');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const marcarLimpia = async (h) => {
    try {
      const { data } = await api.post(`/tenant/hotel/habitaciones/${h.habitacion_id}/limpia`);
      toast.success(data?.aviso || `La ${h.numero} ya está disponible.`);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const PESTANAS = [
    { k: 'hoy', t: 'Hoy', icono: CalendarDays },
    { k: 'buscar', t: 'Buscar y reservar', icono: Search },
    { k: 'reservas', t: 'Reservas', icono: BedDouble },
    { k: 'limpieza', t: `Limpieza${porLimpiar.length ? ` (${porLimpiar.length})` : ''}`, icono: Sparkles },
    { k: 'habitaciones', t: 'Habitaciones', icono: Tag },
    { k: 'temporadas', t: 'Temporadas', icono: Percent },
  ];

  const reservasFiltradas = reservas.filter(r =>
    !filtro.trim() || r.huesped.toLowerCase().includes(filtro.trim().toLowerCase()));

  return (
    <div className="space-y-4" data-testid="hotel-desk">
      <SectionHelp
        module="hotel"
        title="Recepción"
        description={'Aquí se maneja el hospedaje: qué habitaciones tienes, cuánto cuesta cada noche '
            + 'según la temporada, qué hay libre en unas fechas y quién llega o sale hoy. '
            + 'Si tienes la IA conectada a WhatsApp, ella también aparta habitaciones aquí '
            + '— las verás como «Por confirmar» hasta que tú las confirmes.'}
      />

      <div className="flex gap-1 flex-wrap border-b">
        {PESTANAS.map(({ k, t, icono: Icono }) => (
          <button key={k} onClick={() => setPestana(k)}
            className={`px-3 py-2 text-sm flex items-center gap-1.5 border-b-2 -mb-px ${
              pestana === k ? 'border-primary font-medium' : 'border-transparent text-muted-foreground'}`}>
            <Icono className="h-4 w-4" />{t}
          </button>
        ))}
      </div>

      {cargando && <p className="text-sm text-muted-foreground">Cargando…</p>}

      {/* ── HOY ─────────────────────────────────────────────── */}
      {!cargando && pestana === 'hoy' && (
        <div className="space-y-4">
          {/* `=== 0` fallaba mientras el dato aun no llega: un hotel nuevo veia
              un tablero en 0% en vez de las instrucciones. */}
          {!ocupacion?.habitaciones_en_servicio ? (
            <div className="rounded border border-amber-200 bg-amber-50 p-4 text-sm">
              <p className="font-medium">Todavía no has dado de alta tus habitaciones.</p>
              <p className="mt-1 text-muted-foreground">
                Sin habitaciones, el sistema no puede decirte qué tienes libre ni la IA
                puede apartar nada. Empieza en la pestaña «Habitaciones».
              </p>
              <Button size="sm" className="mt-2" onClick={() => setPestana('habitaciones')}>
                Dar de alta habitaciones
              </Button>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-4">
              {[
                ['Ocupación', `${ocupacion?.porcentaje_ocupacion ?? 0}%`],
                ['Ocupadas', ocupacion?.ocupadas ?? 0],
                ['Libres', ocupacion?.libres ?? 0],
                ['En servicio', ocupacion?.habitaciones_en_servicio ?? 0],
              ].map(([t, v]) => (
                <div key={t} className="rounded border p-3">
                  <p className="text-xs text-muted-foreground">{t}</p>
                  <p className="text-2xl font-semibold">{v}</p>
                </div>
              ))}
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            {[
              ['Llegan hoy', ocupacion?.llegan_hoy || [], 'en_casa', LogIn, 'Registrar llegada', 'Llegada registrada'],
              ['Salen hoy', ocupacion?.salen_hoy || [], 'salida', LogOut, 'Registrar salida', 'Salida registrada'],
            ].map(([titulo, lista, destino, Icono, boton, aviso]) => (
              <div key={titulo} className="rounded border">
                <p className="px-3 py-2 border-b text-sm font-medium">{titulo} ({lista.length})</p>
                {lista.length === 0 ? (
                  <p className="p-3 text-sm text-muted-foreground">Nadie por ahora.</p>
                ) : lista.map(r => (
                  <div key={r.id} className="p-3 border-b last:border-0 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.huesped}</p>
                      <p className="text-xs text-muted-foreground">
                        {r.tipo_de_habitacion}{r.numero_de_habitacion ? ` · Hab. ${r.numero_de_habitacion}` : ''}
                        {' · '}{r.noches} {r.noches === 1 ? 'noche' : 'noches'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Etiqueta estado={r.estado} />
                <HelpTooltip text="Su cuenta completa: las noches MÁS lo que ha consumido (room service, minibar, lavandería). Aquí también se le cargan consumos nuevos.">
                  <Button size="sm" variant="outline" onClick={() => abrirCuenta(r)}>
                    <Receipt className="h-4 w-4 mr-1" />Cuenta
                  </Button>
                </HelpTooltip>
                      <Button size="sm" variant="outline"
                        onClick={() => cambiarEstado(r, destino, aviso)}>
                        <Icono className="h-3.5 w-3.5 mr-1" />{boton}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── BUSCAR Y RESERVAR ───────────────────────────────── */}
      {!cargando && pestana === 'buscar' && (
        <div className="space-y-4">
          <div className="rounded border p-3 grid gap-3 sm:grid-cols-4 items-end">
            <div>
              <label className="text-xs flex items-center gap-1">Entrada
                <HelpTooltip text="La primera noche que duerme el huésped." /></label>
              <Input type="date" value={busqueda.entrada}
                onChange={e => setBusqueda({ ...busqueda, entrada: e.target.value })} />
            </div>
            <div>
              <label className="text-xs flex items-center gap-1">Salida
                <HelpTooltip text="El día que se va. Esa noche no se cobra: del 10 al 15 son cinco noches." /></label>
              <Input type="date" value={busqueda.salida}
                onChange={e => setBusqueda({ ...busqueda, salida: e.target.value })} />
            </div>
            <div>
              <label className="text-xs">Personas</label>
              <Input type="number" min="1" value={busqueda.huespedes}
                onChange={e => setBusqueda({ ...busqueda, huespedes: e.target.value })} />
            </div>
            <Button onClick={buscar} disabled={buscando}>
              <Search className="h-4 w-4 mr-1" />{buscando ? 'Buscando…' : 'Ver qué hay libre'}
            </Button>
          </div>

          {disponible && disponible.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No hay nada libre en esas fechas para ese número de personas.
            </p>
          )}

          {(disponible || []).map(g => (
            <div key={g.catalog_item_id} className="rounded border p-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium">{g.tipo_de_habitacion}</p>
                <p className="text-xs text-muted-foreground">
                  {g.disponibles} {g.disponibles === 1 ? 'disponible' : 'disponibles'}
                  {' · '}hasta {g.capacidad} {g.capacidad === 1 ? 'persona' : 'personas'}
                </p>
                {g.cotizacion ? (
                  <p className="text-sm mt-1">
                    <span className="font-semibold">{g.cotizacion.total} {g.cotizacion.moneda}</span>
                    <span className="text-muted-foreground">
                      {' '}por {g.cotizacion.noches} {g.cotizacion.noches === 1 ? 'noche' : 'noches'}
                    </span>
                  </p>
                ) : (
                  <p className="text-sm mt-1 text-amber-700">{g.sin_precio}</p>
                )}
              </div>
              <Button size="sm" disabled={!g.cotizacion} onClick={() => setReservando(g)}>
                Reservar
              </Button>
            </div>
          ))}

          {reservando && (
            <div className="rounded border p-3 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-medium">Reservar {reservando.tipo_de_habitacion}</p>
                <Button size="sm" variant="ghost" onClick={() => setReservando(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                <Input placeholder="Nombre del huésped" value={huesped.guest_name}
                  onChange={e => setHuesped({ ...huesped, guest_name: e.target.value })} />
                <Input placeholder="Teléfono (opcional)" value={huesped.guest_phone}
                  onChange={e => setHuesped({ ...huesped, guest_phone: e.target.value })} />
                <Input placeholder="Correo (opcional)" value={huesped.guest_email}
                  onChange={e => setHuesped({ ...huesped, guest_email: e.target.value })} />
              </div>
              <Button onClick={reservar}>Confirmar reserva</Button>
            </div>
          )}
        </div>
      )}

      {/* ── RESERVAS ────────────────────────────────────────── */}
      {!cargando && pestana === 'reservas' && (
        <div className="space-y-3">
          <Input placeholder="Buscar por nombre del huésped…" value={filtro}
            onChange={e => setFiltro(e.target.value)} className="max-w-sm" />
          {reservasFiltradas.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No hay reservas de hoy en adelante.
            </p>
          ) : reservasFiltradas.map(r => (
            <div key={r.id} className="rounded border p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{r.huesped}</p>
                <p className="text-xs text-muted-foreground">
                  {r.entrada} → {r.salida} · {r.noches} {r.noches === 1 ? 'noche' : 'noches'}
                  {' · '}{r.tipo_de_habitacion}
                  {r.numero_de_habitacion ? ` · Hab. ${r.numero_de_habitacion}` : ''}
                  {r.total ? ` · ${r.total}` : ''}
                  {r.origen === 'ia' ? ' · apartada por la IA' : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Etiqueta estado={r.estado} />
                {r.estado === 'pendiente' && (
                  <Button size="sm" onClick={() => cambiarEstado(r, 'confirmada', 'Confirmada')}>
                    Confirmar
                  </Button>
                )}
                {['pendiente', 'confirmada'].includes(r.estado) && (
                  <Button size="sm" variant="outline"
                    onClick={() => cambiarEstado(r, 'cancelada', 'Cancelada')}>
                    Cancelar
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── LA CUENTA DE LA ESTANCIA (S136) ─────────────────── */}
      {cuenta && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             onClick={() => setCuenta(null)}>
          <div className="bg-background rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto p-4 space-y-3"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-semibold">Cuenta de {cuenta.huesped}</h3>
                <p className="text-xs text-muted-foreground">
                  Las noches más lo que ha consumido.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setCuenta(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="rounded-lg border divide-y text-sm">
              <div className="flex justify-between p-2">
                <span>Hospedaje</span><span>${cuenta.hospedaje}</span>
              </div>
              <div className="flex justify-between p-2">
                <span>Consumos</span><span>${cuenta.consumos}</span>
              </div>
              <div className="flex justify-between p-2 font-semibold">
                <span>Total</span><span>${cuenta.total}</span>
              </div>
              <div className="flex justify-between p-2 text-muted-foreground">
                <span>Ya pagó</span><span>${cuenta.pagado}</span>
              </div>
              <div className="flex justify-between p-2 font-semibold text-emerald-700">
                <span>Falta por cobrar</span><span>${cuenta.por_cobrar}</span>
              </div>
            </div>

            {/* Cargar algo nuevo. Solo si sigue hospedado: a quien ya salió no
                se le puede cargar nada, y el servidor lo rechaza — mostrar el
                formulario sería ofrecer algo que va a fallar. */}
            {cuenta.estado === 'en_casa' ? (
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-sm font-medium">Cargarle un consumo</p>
                <div className="grid gap-2 sm:grid-cols-[1fr_80px_110px_auto]">
                  <Input placeholder="Ej. Room service — desayuno"
                    value={nuevoConsumo.descripcion}
                    onChange={(e) => setNuevoConsumo({ ...nuevoConsumo, descripcion: e.target.value })} />
                  <Input type="number" min={1} placeholder="Cant."
                    value={nuevoConsumo.cantidad}
                    onChange={(e) => setNuevoConsumo({ ...nuevoConsumo, cantidad: e.target.value })} />
                  <Input type="number" min={0} step="0.01" placeholder="Precio"
                    value={nuevoConsumo.precio_unitario}
                    onChange={(e) => setNuevoConsumo({ ...nuevoConsumo, precio_unitario: e.target.value })} />
                  <Button onClick={cargarConsumo}>
                    <Plus className="h-4 w-4 mr-1" />Cargar
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Queda pendiente en su cuenta y se cobra al hacer la salida.
                </p>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground rounded-lg border bg-muted/40 p-3">
                Solo se le puede cargar un consumo a quien está hospedado.
                Esta reservación está en «{cuenta.estado}».
              </p>
            )}

            <div className="space-y-1">
              <p className="text-sm font-medium">Sus consumos</p>
              {cuenta.cargos.length === 0 && (
                <p className="text-xs text-muted-foreground">Todavía no ha consumido nada.</p>
              )}
              {cuenta.cargos.map((c) => (
                <div key={c.id}
                     className={`flex items-center justify-between gap-2 rounded border p-2 text-sm ${
                       c.estado === 'anulado' ? 'opacity-60' : ''}`}>
                  <div className="min-w-0">
                    <p className={`truncate ${c.estado === 'anulado' ? 'line-through' : ''}`}>
                      {c.descripcion}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {c.cantidad} × ${c.precio_unitario}
                      {c.estado === 'anulado' && c.motivo_anulacion
                        ? ` · anulado: ${c.motivo_anulacion}`
                        : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-medium">${c.total}</span>
                    {c.estado === 'pendiente' && (
                      <Button variant="ghost" size="sm" onClick={() => anularConsumo(c)}>
                        Quitar
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── LIMPIEZA (ama de llaves) ────────────────────────── */}
      {!cargando && pestana === 'limpieza' && (
        <div className="space-y-3">
          <div className="rounded-lg border bg-muted/40 p-3 text-sm">
            <p className="font-medium">Qué es esta lista</p>
            <p className="text-muted-foreground mt-1">
              Cuando marcas la salida de un huésped, su habitación <b>no vuelve
              a venderse de inmediato</b>: se queda aquí hasta que alguien la
              limpie. Así recepción no puede asignarle a otro huésped un cuarto
              que todavía está sucio. En cuanto marques una como limpia, vuelve
              al inventario al instante.
            </p>
          </div>

          {porLimpiar.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No hay nada pendiente de limpieza. Todas las habitaciones que se
              desocuparon ya están listas para vender.
            </p>
          )}

          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {porLimpiar.map((h) => (
              <div key={h.habitacion_id} className="border rounded-lg p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold">Habitación {h.numero}</p>
                  <p className="text-xs text-muted-foreground truncate">{h.tipo}</p>
                  <p className="text-xs mt-1 text-amber-700">
                    {h.minutos_esperando == null
                      ? 'Esperando limpieza'
                      : h.minutos_esperando < 60
                        ? `Esperando ${h.minutos_esperando} min`
                        : `Esperando ${Math.floor(h.minutos_esperando / 60)} h ${h.minutos_esperando % 60} min`}
                  </p>
                </div>
                <HelpTooltip text="Con esto la habitación vuelve al inventario y recepción ya puede asignársela a alguien. Queda registrado tu nombre y la hora.">
                  <Button size="sm" onClick={() => marcarLimpia(h)}>
                    <Sparkles className="h-4 w-4 mr-1" />Ya está limpia
                  </Button>
                </HelpTooltip>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── HABITACIONES ────────────────────────────────────── */}
      {!cargando && pestana === 'habitaciones' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Cada habitación física con su número. Dos habitaciones pueden ser
              del mismo tipo y aun así solo una queda libre esa noche.
            </p>
            <Button size="sm" onClick={() => setNuevaHab({ catalog_item_id: '', number: '', floor: '', status: 'disponible' })}>
              <Plus className="h-4 w-4 mr-1" />Agregar
            </Button>
          </div>

          {tipos.length === 0 && (
            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm">
              Antes de dar de alta habitaciones necesitas al menos un <strong>tipo</strong> en
              tu Catálogo (por ejemplo «Doble vista al mar»). El tipo lleva el precio y las fotos;
              la habitación lleva el número.
            </div>
          )}

          {nuevaHab && (
            <div className="rounded border p-3 grid gap-2 sm:grid-cols-5 items-end">
              <div className="sm:col-span-2">
                <label className="text-xs">Tipo de habitación</label>
                <Select value={nuevaHab.catalog_item_id}
                  onValueChange={v => setNuevaHab({ ...nuevaHab, catalog_item_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Elige el tipo" /></SelectTrigger>
                  <SelectContent>
                    {tipos.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs">Número</label>
                <Input value={nuevaHab.number} placeholder="101"
                  onChange={e => setNuevaHab({ ...nuevaHab, number: e.target.value })} />
              </div>
              <div>
                <label className="text-xs">Piso</label>
                <Input value={nuevaHab.floor} placeholder="1"
                  onChange={e => setNuevaHab({ ...nuevaHab, floor: e.target.value })} />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={guardarHabitacion}>Guardar</Button>
                <Button size="sm" variant="ghost" onClick={() => setNuevaHab(null)}>Cancelar</Button>
              </div>
            </div>
          )}

          {habitaciones.map(h => (
            <div key={h.id} className="rounded border p-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">
                  Hab. {h.numero}{h.piso ? ` · piso ${h.piso}` : ''}
                </p>
                <p className="text-xs text-muted-foreground">
                  {h.tipo_de_habitacion} · {ESTADO_HABITACION[h.estado] || h.estado}
                </p>
              </div>
              <Button size="sm" variant="ghost"
                onClick={() => borrar(`/tenant/hotel/habitaciones/${h.id}`, 'Habitación eliminada.')}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* ── TEMPORADAS ──────────────────────────────────────── */}
      {!cargando && pestana === 'temporadas' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              El precio de una noche cambia con la fecha. Si dos temporadas se
              enciman —Navidad dentro de temporada alta—, gana la de mayor prioridad.
            </p>
            <Button size="sm" onClick={() => setNuevaTemp({
              catalog_item_id: '', name: '', starts_on: hoyISO(), ends_on: enDias(30),
              price_per_night: '', priority: 0,
            })}>
              <Plus className="h-4 w-4 mr-1" />Agregar
            </Button>
          </div>

          {nuevaTemp && (
            <div className="rounded border p-3 grid gap-2 sm:grid-cols-6 items-end">
              <div className="sm:col-span-2">
                <label className="text-xs">Aplica a</label>
                <Select value={nuevaTemp.catalog_item_id}
                  onValueChange={v => setNuevaTemp({ ...nuevaTemp, catalog_item_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Tipo de habitación" /></SelectTrigger>
                  <SelectContent>
                    {tipos.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs">Nombre</label>
                <Input value={nuevaTemp.name} placeholder="Temporada alta"
                  onChange={e => setNuevaTemp({ ...nuevaTemp, name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs">Desde</label>
                <Input type="date" value={nuevaTemp.starts_on}
                  onChange={e => setNuevaTemp({ ...nuevaTemp, starts_on: e.target.value })} />
              </div>
              <div>
                <label className="text-xs">Hasta</label>
                <Input type="date" value={nuevaTemp.ends_on}
                  onChange={e => setNuevaTemp({ ...nuevaTemp, ends_on: e.target.value })} />
              </div>
              <div>
                <label className="text-xs flex items-center gap-1">Por noche
                  <HelpTooltip text="Lo que cuesta una noche en esas fechas." /></label>
                <Input type="number" value={nuevaTemp.price_per_night}
                  onChange={e => setNuevaTemp({ ...nuevaTemp, price_per_night: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs flex items-center gap-1">Prioridad
                  <HelpTooltip text="Si dos temporadas cubren la misma fecha, gana el número más alto. Navidad dentro de temporada alta: ponle prioridad mayor a Navidad." /></label>
                <Input type="number" value={nuevaTemp.priority}
                  onChange={e => setNuevaTemp({ ...nuevaTemp, priority: e.target.value })} />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={guardarTemporada}>Guardar</Button>
                <Button size="sm" variant="ghost" onClick={() => setNuevaTemp(null)}>Cancelar</Button>
              </div>
            </div>
          )}

          {temporadas.length === 0 && !nuevaTemp && (
            <p className="text-sm text-muted-foreground">
              Sin temporadas, se cobra el precio que tenga el tipo de habitación en el Catálogo.
            </p>
          )}

          {temporadas.map(t => (
            <div key={t.id} className="rounded border p-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{t.nombre}</p>
                <p className="text-xs text-muted-foreground">
                  {t.desde} → {t.hasta} · {t.precio_por_noche} por noche · prioridad {t.prioridad}
                </p>
              </div>
              <Button size="sm" variant="ghost"
                onClick={() => borrar(`/tenant/hotel/temporadas/${t.id}`, 'Temporada eliminada.')}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
